import React from "react";
import { useDispatch } from "react-redux";
import {
  TextField,
  Grid,
  Select,
  MenuItem,
  FormControl,
  Typography,
  InputLabel,
  Stepper,
  Step,
  StepButton,
  Radio,
  Slider,
} from "@material-ui/core";
import { getLocalizedString } from "@icarius-localization/strings";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import { createRecognitionPlanAction, updateRecognitionPlanAction } from "../../actions";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import useHandleForm from "./useHandleForm";
import useHandleStepper from "@icarius-utils/hooks/useHandleStepper";
import MedalAndShieldContainer from "./medalAndShieldContainer";
import CustomNumberFormat from '@icarius-common/dynamicInputs/customNumberFormat';

const gridStyle = { height: 75, padding: "0px 10px" };

const StepContainer = ({ title, children }) => {
  return (
    <Grid container item xs={12} justify="center">
      <Grid container item sm={12} md={10}>
        <Typography className="whiteText" style={{ fontSize: 20, width: '100%', padding: '0px 10px' }}>
          {title}
        </Typography>
        <Grid container item alignItems="center" justify="space-between" xs={12} style={{ paddingTop: 10 }}>
          {children}
        </Grid>
      </Grid>
    </Grid>
  )
}

const Form = (props) => {

  const {
    data,
    statusList,
    changePointsList,
    priceAwardingList,
    diffusionList,
    annotationList,
    handleClose,
  } = props;
  const dispatch = useDispatch();

  const create = (dataToSend) => {
    dispatch(createRecognitionPlanAction(dataToSend))
      .then((res) => res?.status === 200 && handleClose());
  }

  const modify = (dataToSend) => {
    dispatch(updateRecognitionPlanAction(dataToSend))
      .then((res) => res?.status === 200 && handleClose());
  }

  const openValidationError = () => {
    dispatch(openSnackbarAction({ msg: getLocalizedString("invalidData"), severity: "error", duration: 10000 }));
  }

  const {
    isCreate,
    formData,
    setFormValue,
    submit,
    stepOneIsValid,
    stepTwoIsValid,
    stepThreeIsValid,
    stepFourIsValid,
  } = useHandleForm(data, create, modify, openValidationError);

  const steps = [
    {
      name: getLocalizedString("planIdentification"),
      render: () => {
        return (
          <StepContainer title={getLocalizedString("pleaseSpecifyNewRecognitionPlanParameters")}>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <TextField
                disabled={!isCreate}
                required
                fullWidth
                margin={"none"}
                label={getLocalizedString("centerCode")}
                value={formData.code}
                inputProps={{ maxLength: 20 }}
                onChange={(e) => setFormValue(e.target.value, "code")}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <TextField
                required
                fullWidth
                margin={"none"}
                label={getLocalizedString("name")}
                value={formData.name}
                inputProps={{ maxLength: 100 }}
                onChange={(e) => setFormValue(e.target.value, "name")}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <TextField
                required
                label={getLocalizedString("valueOfEachPoint")}
                value={formData.pointValue}
                fullWidth
                margin={"none"}
                onChange={(e) => setFormValue(e.target.value, 'pointValue')}
                InputProps={{ inputComponent: CustomNumberFormat }}
                inputProps={{ min: "0", step: "0.01", decimals: 2 }}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <TextField
                label={getLocalizedString("maxPlanBudgetInCash")}
                value={formData.maxBudget}
                fullWidth
                margin={"none"}
                onChange={(e) => setFormValue(e.target.value, 'maxBudget')}
                InputProps={{ inputComponent: CustomNumberFormat }}
                inputProps={{ min: "0", step: "0.01", decimals: 2 }}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <TextField
                required
                label={getLocalizedString("specificWeight")}
                value={formData.specificWeight}
                fullWidth
                margin={"none"}
                InputProps={{ inputComponent: CustomNumberFormat }}
                onChange={(e) => setFormValue(e.target.value, 'specificWeight')}
                inputProps={{ min: "1", step: "0.01", decimals: 2 }}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel id={'label-status'}>{getLocalizedString("planStatus")}</InputLabel>
                <Select
                  value={formData.status}
                  labelId={'label-status'}
                  id={'select-status'}
                  onChange={(e) => setFormValue(e.target.value, "status")}
                  margin={"none"}
                >
                  {
                    statusList.map(item => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.key}
                        value={item.key}>
                        {item.value}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
          </StepContainer>
        )
      }
    },
    {
      name: getLocalizedString("pointsAndRedemptionScheme"),
      render: () => {
        const fields = [
          ["toMedalBronzePoints", "changePointsMedalBronze"],
          ["toMedalSilverPoints", "changePointsMedalSilver"],
          ["toMedalGoldPoints", "changePointsMedalGold"],
          ["toShieldBronzePoints", "changePointsShieldBronze"],
          ["toShieldSilverPoints", "changePointsShieldSilver"],
          ["toShieldGoldPoints", "changePointsShieldGold"],
        ];

        const slots = fields.map(([field1, field2], index) => {
          return (
            <div key={index}>
              <Grid container item alignItems="center" xs={12} style={gridStyle}>
                <TextField
                  label={getLocalizedString("pointsToGetIt")}
                  value={formData[field1]}
                  fullWidth
                  margin={"none"}
                  InputProps={{ inputComponent: CustomNumberFormat }}
                  onChange={(e) => setFormValue(e.target.value, field1)}
                  inputProps={{ min: "0", step: "1", decimals: 0 }}
                />
              </Grid>
              <Grid container item alignItems="center" xs={12} style={gridStyle}>
                <FormControl style={{ width: "100%" }}>
                  <InputLabel id={`label-${field2}`}>{getLocalizedString("tradeForPoints")}</InputLabel>
                  <Select
                    value={formData[field2]}
                    labelId={`label-${field2}`}
                    id={`select-${field2}`}
                    onChange={(e) => setFormValue(e.target.value, field2)}
                    margin={"none"}
                  >
                    {
                      changePointsList.map(item => (
                        <MenuItem
                          className={"whiteText"}
                          key={item.key}
                          value={item.key}>
                          {item.value}
                        </MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </Grid>
            </div>
          )
        });

        return (
          <StepContainer title={getLocalizedString("choosePointAmountAndRedemptionType")}>
            <MedalAndShieldContainer
              slot1={slots[0]}
              slot2={slots[1]}
              slot3={slots[2]}
              slot4={slots[3]}
              slot5={slots[4]}
              slot6={slots[5]}
            />
          </StepContainer>
        )
      },
    },
    {
      name: getLocalizedString("exchange"),
      render: () => {
        const fields = [
          ["obligationChangeMedalBronze", "percentMoneyChangeMedalBronze"],
          ["obligationChangeMedalSilver", "percentMoneyChangeMedalSilver"],
          ["obligationChangeMedalGold", "percentMoneyChangeMedalGold"],
          ["obligationChangeShieldBronze", "percentMoneyChangeShieldBronze"],
          ["obligationChangeShieldSilver", "percentMoneyChangeShieldSilver"],
          ["obligationChangeShieldGold", "percentMoneyChangeShieldGold"],
        ];

        const slots = fields.map(([field1, field2], index) => {
          return (
            <div key={index}>
              <Grid container item alignItems="center" xs={12}>
                <div style={{ display: 'flex', alignItems: 'center', marginRight: 20 }} onClick={() => setFormValue('Y', field1)}>
                  <Radio color='primary' checked={formData[field1] !== "N"} />
                  <Typography className="whiteText" variant="subtitle1" style={{ display: "inline-block" }}>
                    {getLocalizedString("annexRequired")}
                  </Typography>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }} onClick={() => setFormValue('N', field1)}>
                  <Radio color='primary' checked={formData[field1] !== "Y"} />
                  <Typography className="whiteText" variant="subtitle1" style={{ display: "inline-block" }}>
                    {getLocalizedString("optional")}
                  </Typography>
                </div>
              </Grid>
              <Grid container item alignItems="center" xs={12} style={{ ...gridStyle, height: '' }}>
                <Typography className="whiteText" variant="subtitle1" style={{ display: "inline-block" }}>
                  {getLocalizedString("maxPercentageMoneyTrade")}
                </Typography>
                <Slider
                  onChange={(_, value) => setFormValue(value, field2)}
                  min={0}
                  max={100}
                  step={1}
                  defaultValue={0}
                  value={parseInt(formData[field2])}
                />
                <Typography className="whiteText" variant="subtitle1" align="center" style={{ width: '100%' }}>
                  {parseInt(formData[field2])}%
                </Typography>
              </Grid>
            </div>
          )
        });

        return (
          <StepContainer title={getLocalizedString("selectRedemptionFormat")}>
            <MedalAndShieldContainer
              slot1={slots[0]}
              slot2={slots[1]}
              slot3={slots[2]}
              slot4={slots[3]}
              slot5={slots[4]}
              slot6={slots[5]}
            />
          </StepContainer>
        )
      },
    },
    {
      name: getLocalizedString("awardsAndPromotion"),
      render: () => {
        const fields = [
          ["priceAwardingMedalBronze", "diffusionMedalBronze"],
          ["priceAwardingMedalSilver", "diffusionMedalSilver"],
          ["priceAwardingMedalGold", "diffusionMedalGold"],
          ["priceAwardingShieldBronze", "diffusionShieldBronze"],
          ["priceAwardingShieldSilver", "diffusionShieldSilver"],
          ["priceAwardingShieldGold", "diffusionShieldGold"],
        ];

        const slots = fields.map(([field1, field2], index) => {
          return (
            <div key={index}>
              <Grid container item alignItems="center" xs={12} style={gridStyle}>
                <FormControl style={{ width: "100%" }}>
                  <InputLabel id={`label-${field1}`}>{getLocalizedString("awardDelivery")}</InputLabel>
                  <Select
                    value={formData[field1]}
                    labelId={`label-${field1}`}
                    id={`select-${field1}`}
                    onChange={(e) => setFormValue(e.target.value, field1)}
                    margin={"none"}
                  >
                    {
                      priceAwardingList.map(item => (
                        <MenuItem
                          className={"whiteText"}
                          key={item.key}
                          value={item.key}>
                          {item.value}
                        </MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </Grid>
              <Grid container item alignItems="center" xs={12} style={gridStyle}>
                <FormControl style={{ width: "100%" }}>
                  <InputLabel id={`label-${field2}`}>{getLocalizedString("dissemination")}</InputLabel>
                  <Select
                    value={formData[field2]}
                    labelId={`label-${field2}`}
                    id={`select-${field2}`}
                    onChange={(e) => setFormValue(e.target.value, field2)}
                    margin={"none"}
                  >
                    {
                      diffusionList.map(item => (
                        <MenuItem
                          className={"whiteText"}
                          key={item.key}
                          value={item.key}>
                          {item.value}
                        </MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </Grid>
            </div>
          )
        });

        return (
          <StepContainer title={getLocalizedString("chooseAwardDeliveryAndDiffusionType")}>
            <MedalAndShieldContainer
              slot1={slots[0]}
              slot2={slots[1]}
              slot3={slots[2]}
              slot4={slots[3]}
              slot5={slots[4]}
              slot6={slots[5]}
            />
          </StepContainer>
        )
      },
    },
    {
      name: getLocalizedString("annotationsRegistration"),
      render: () => {
        const fields = [
          "annotationRecordMedalBronze",
          "annotationRecordMedalSilver",
          "annotationRecordMedalGold",
          "annotationRecordShieldBronze",
          "annotationRecordShieldSilver",
          "annotationRecordShieldGold",
        ];

        const slots = fields.map((field1, index) => {
          return (
            <div key={index}>
              <Grid container item alignItems="center" xs={12} style={gridStyle}>
                <FormControl style={{ width: "100%" }}>
                  <InputLabel id={`label-${field1}`}>{getLocalizedString("criticalIncidentLogShort")}</InputLabel>
                  <Select
                    value={formData[field1]}
                    labelId={`label-${field1}`}
                    id={`select-${field1}`}
                    onChange={(e) => setFormValue(e.target.value, field1)}
                    margin={"none"}
                  >
                    {
                      annotationList.map(item => (
                        <MenuItem
                          className={"whiteText"}
                          key={item.key}
                          value={item.key}>
                          {item.value}
                        </MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </Grid>
            </div>
          )
        });

        return (
          <StepContainer title={getLocalizedString("chooseHowRecordWillAppearInNotes")}>
            <MedalAndShieldContainer
              slot1={slots[0]}
              slot2={slots[1]}
              slot3={slots[2]}
              slot4={slots[3]}
              slot5={slots[4]}
              slot6={slots[5]}
            />
          </StepContainer>
        )
      },
    },
  ];

  const {
    currentStep,
    setCurrentStep,
    setNextStep,
    setPreviousStep,
    renderStep,
  } = useHandleStepper(steps);

  const handleSetNextStep = async () => {
    let isValid = await [stepOneIsValid, stepTwoIsValid, stepThreeIsValid, stepFourIsValid][currentStep]();

    if (!isValid) {
      openValidationError();
      return;
    }

    setNextStep();
  }

  return (
    <>
      <Grid container item xs={12} direction="row" style={{ paddingTop: 10 }}>
        <Grid container item xs={12} justify="center">
          <Stepper activeStep={currentStep} alternativeLabel style={{ width: '100%' }}>
            {
              steps.map((step, index) => {
                return (
                  <Step key={index}>
                    <StepButton onClick={() => index < currentStep && setCurrentStep(index)}>{step.name}</StepButton>
                  </Step>
                );
              })
            }
          </Stepper>
        </Grid>
        <Grid container item xs={12}>
          {renderStep()}
        </Grid>
      </Grid>
      <Grid container item sm={12} justify="center">
        <Grid container item sm={12} md={10} justify={"flex-end"} alignItems="center" style={{ paddingBottom: 20, paddingTop: 10, paddingRight: 10 }}>
          {
            (currentStep !== 0) &&
            <div style={{ marginRight: 10 }}>
              <ButtonDialogAction isAccept onClick={setPreviousStep} text={getLocalizedString("previous")} />
            </div>
          }
          {
            (currentStep === steps.length - 1)
              ? <ButtonDialogAction isAccept onClick={submit} text={getLocalizedString("save")} />
              : <ButtonDialogAction isAccept onClick={handleSetNextStep} text={getLocalizedString("next")} />
          }
        </Grid>
      </Grid>
    </>
  );
}



export default Form;
